import { Box, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { API_BASE_URL, authFetchJson } from '../../apiClient.ts';
import { Link } from 'react-router-dom';
import type { PipelineRow } from '../../types/PipelineRow.ts';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { OPPORTUNITY_STATUS_LABELS } from '../../types/OpportunityStatusChoices.ts';
import { compareReportDates, formatReportDate } from '../../utils/helperUtils.ts';

interface Props {
  isDashboard?: boolean;
}

export const LostButTrackReport: React.FunctionComponent<Props> = (props) => {
  const { isDashboard = false } = props;
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDashboard) {
      authFetchJson(`${API_BASE_URL}/dashboard/lost-but-tracked`)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error loading dashboard data:', error);
          setLoading(false);
        });
    } else {
      authFetchJson(`${API_BASE_URL}/reports/lost-but-tracked`)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load pipeline report', err);
          setLoading(false);
        });
    }
  }, [isDashboard]);

  const exportToCSV = () => {
    const headers = ['Closed', 'Follow Up Date', 'Status', 'Contact', 'Opportunity'];
    const csvRows = rows.map((row) => [
      formatReportDate(row.dateClosed),
      formatReportDate(row.followUpDate),
      row.status,
      row.contactName,
      row.opportunityName
    ]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map((r) => r.map((cell) => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `lost-but-tracked-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'dateClosed',
      headerName: 'Closed',
      width: 175,
      renderCell: (params) => formatReportDate(params.row?.dateClosed),
      sortComparator: compareReportDates
    },
    {
      field: 'followUpDate',
      headerName: 'Follow-Up Date',
      width: 175,
      renderCell: (params) => formatReportDate(params.row?.dateOpened),
      sortComparator: compareReportDates
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 175,
      renderCell: (params) => {
        const status = params.row?.status;
        return OPPORTUNITY_STATUS_LABELS[status] ?? status;
      }
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      width: 250,
      renderCell: (params) => (
        <MuiLink component={Link} target={'_blank'} to={`/contacts/${params.row.contactId}/show`}>
          {params.value}
        </MuiLink>
      )
    },
    {
      field: 'opportunityName',
      headerName: 'Opportunity',
      flex: 1,
      renderCell: (params) => (
        <MuiLink component={Link} target={'_blank'} to={`/opportunities/${params.row.id}/show`}>
          {params.value}
        </MuiLink>
      )
    }
  ];

  return (
    <>
      <Box>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {!isDashboard && (
              <Box mb={2}>
                <Button variant="outlined" onClick={exportToCSV}>
                  Download CSV
                </Button>
              </Box>
            )}

            <Box sx={{ height: isDashboard ? 320 : 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pagination
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: isDashboard ? 10 : 25, page: 0 } },
                  sorting: {
                    sortModel: [{ field: 'dateOpened', sort: 'desc' }]
                  }
                }}
                density={isDashboard ? 'compact' : 'standard'}
                disableRowSelectionOnClick
              />
            </Box>
          </>
        )}
      </Box>
    </>
  );
};
